import { useContext, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ViberIcon from '../icons/Viber';
import { GlobalContext } from '../context/GlobalState';
import { messengerUrls, stringConstants, viberDownloadUrl } from 'constants';
import { copyToClipboard } from '../utils/clipboard';
import { strings } from 'localisation';

// Brand colours rather than the site accent: a purple circle and a green one are recognised as
// "Viber" and "WhatsApp" before the glyph inside them is read, which is the whole job of a
// floating chat button. Viber sits on top because it is the one the rest of the site names
// first, and because Bulgarian customers reach for it more often.
//
// iconSize differs per channel on purpose. Both marks are drawn on a 24x24 grid, but the Viber
// path runs edge to edge while the MUI WhatsApp glyph keeps a 2px margin, so matched font sizes
// would leave Viber looking noticeably fatter.
const channels = [
  {
    key: 'viber',
    labelKey: 'writeOnViber',
    href: messengerUrls.viber,
    Icon: ViberIcon,
    iconSize: 23,
    rest: '#7360F2',
    hover: '#5B4AD4',
    // viber:// is claimed by the app, not loaded as a page. Opening it in a new tab strands an
    // empty one behind it on desktop, so this link stays in the current tab.
    newTab: false
  },
  {
    key: 'whatsapp',
    labelKey: 'writeOnWhatsApp',
    href: messengerUrls.whatsapp,
    Icon: WhatsAppIcon,
    iconSize: 26,
    rest: '#25D366',
    hover: '#1EA952',
    newTab: true
  }
];

// Viber publishes no https link - viber:// is the only way in, and unlike wa.me it has nothing
// to fall back on when the app is missing. The browser will not tell us either: it logs
// "the scheme does not have a registered handler" to the console and leaves the page untouched,
// and that error is not observable from script. The one thing we can see is focus. Launching
// the app - or even Chrome's "Open Viber?" prompt - takes focus off the document, so if we
// still hold it a beat after the click, nothing answered and we owe the customer the number.
const VIBER_LAUNCH_GRACE_MS = 2200;
const COPIED_FEEDBACK_MS = 2000;

export default function MessengerButtons() {
  const { lang } = useContext(GlobalContext);
  const [viberMissing, setViberMissing] = useState(false);
  const [copied, setCopied] = useState(false);
  // Holds the teardown for whichever watch is in flight, so a click that never resolves cannot
  // outlive the component or stack a second set of listeners on top of the first.
  const cancelWatchRef = useRef(null);

  useEffect(() => () => cancelWatchRef.current && cancelWatchRef.current(), []);

  const watchForViberLaunch = () => {
    if (cancelWatchRef.current) return;

    let timer;
    // Any of these means something took over: the app, the browser's own prompt, a tab switch.
    const stopWatching = () => {
      clearTimeout(timer);
      window.removeEventListener('blur', stopWatching);
      window.removeEventListener('pagehide', stopWatching);
      document.removeEventListener('visibilitychange', stopWatching);
      cancelWatchRef.current = null;
    };

    window.addEventListener('blur', stopWatching);
    window.addEventListener('pagehide', stopWatching);
    document.addEventListener('visibilitychange', stopWatching);
    cancelWatchRef.current = stopWatching;

    timer = setTimeout(() => {
      const stillHere = document.visibilityState === 'visible' && document.hasFocus();
      stopWatching();
      // Silence is the only evidence, so err towards staying quiet: a missed dialog costs less
      // than one thrown over a chat window that did open.
      if (stillHere) setViberMissing(true);
    }, VIBER_LAUNCH_GRACE_MS);
  };

  const label = (key) => strings[lang]?.[key] || strings.default[key];

  const closeDialog = () => {
    setViberMissing(false);
    setCopied(false);
  };

  // copyToClipboard has a hidden-textarea path for the browsers with no clipboard API, so the
  // button is worth offering everywhere. Confirm only on a true - a silent failure leaves the
  // number sitting there selectable, which is the honest outcome.
  const copyNumber = async () => {
    if (!(await copyToClipboard(stringConstants.phone))) return;
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <>
      {/* Pinned bottom-right, which keeps the column clear of the bottom-centred back-to-top
          button on phones. The column itself ignores taps so the gap between the two circles
          never swallows a click meant for the page underneath. */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 'calc(16px + env(safe-area-inset-bottom))',
          right: 'calc(16px + env(safe-area-inset-right))',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pointerEvents: 'none',
          zIndex: (t) => t.zIndex.fab
        }}>
        {channels.map(({ key, labelKey, href, Icon, iconSize, rest, hover, newTab }) => (
          <Fab
            key={key}
            size="medium"
            href={href}
            target={newTab ? '_blank' : undefined}
            rel={newTab ? 'noreferrer' : undefined}
            onClick={key === 'viber' ? watchForViberLaunch : undefined}
            aria-label={label(labelKey)}
            title={label(labelKey)}
            sx={{
              pointerEvents: 'auto',
              color: '#FFFFFF',
              backgroundColor: rest,
              '&:hover': { backgroundColor: hover }
            }}>
            <Icon sx={{ fontSize: iconSize }} />
          </Fab>
        ))}
      </Box>

      <Dialog open={viberMissing} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{label('viberNotFoundTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {label('viberNotFoundBody')}
          </Typography>
          {/* Copy sits against the number rather than down in the action row - it is the number
              being copied, and a third button down there wrapped the row onto two lines. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              py: 1.5,
              px: 2,
              backgroundColor: 'background.tile'
            }}>
            {/* index.css turns selection off for the whole document to keep the photography from
                being dragged out of the page. A number nobody can highlight is useless, so this
                one opts back in - the copy button is the shortcut, not the only way. */}
            <Typography
              component="span"
              sx={{
                fontSize: 22,
                fontWeight: 600,
                WebkitUserSelect: 'text',
                userSelect: 'text'
              }}>
              {stringConstants.phone}
            </Typography>
            <IconButton
              onClick={copyNumber}
              size="small"
              aria-label={label('copyNumber')}
              title={label('copyNumber')}>
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Box>
          {/* Reserved height, so confirming the copy does not shunt the buttons down a row. */}
          <Typography
            variant="caption"
            color="success.main"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 0.75,
              minHeight: 18,
              visibility: copied ? 'visible' : 'hidden'
            }}>
            {label('numberCopied')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            href={viberDownloadUrl}
            target="_blank"
            rel="noreferrer"
            sx={{ mr: 'auto', color: '#7360F2' }}>
            {label('downloadViber')}
          </Button>
          <Button variant="contained" onClick={closeDialog}>
            {label('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
